import { useSyncExternalStore } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { Link } from "../components/LocalizedLink";
import { ArrowUpRight } from "lucide-react";
import "./css/lead.css";
import { V3_SECTIONS } from "./pages.js";
import { V3_PLACEHOLDER } from "./menu";

// Unsichtbare Umbruchstellen (<wbr>) an Wortfugen langer Komposita, nur beim Rendern (nicht im Text der Seitenliste
// oder im Seitentitel). So bricht eine Überschrift auf dem Handy an der Fuge statt mitten im Wort, ganz ohne Trennstrich.
const JOINTS = [
    ["Rohstoff", "rückgewinnung"], ["Betriebs", "schließungen"], ["Werks", "schließung"], ["Standort", "schließung"],
    ["Geschäfts", "führung"], ["Daten", "schutz", "erklärung"], ["Metall", "bearbeitungs", "maschinen"],
    ["Holz", "bearbeitungs", "maschinen"], ["Kunststoff", "maschinen"], ["Maschinen", "ersatzteile"],
    ["Produktions", "anlagen"], ["Industrie", "elektronik"], ["Antriebs", "technik"], ["Frequenz", "umrichter"],
    ["Servo", "motoren"], ["Langsam", "dreher"], ["Bestands", "analyse"],
    ["Instand", "haltung"], ["Industrie", "komponenten"], ["Über", "bestände"], ["Alt", "bestände"],
    ["Nachhaltig", "keit"], ["Kreislauf", "wirtschaft"],
];
const JOINT_MAP = new Map(JOINTS.map((parts) => [parts.join(""), parts]));

// Umbruchstellen nur auf schmalen Bildschirmen (unter 480 px). Darüber brechen Überschriften und Links
// nur zwischen ganzen Wörtern (Wunsch: kein „Rohstoff / rückgewinnung“ auf dem Desktop).
const NARROW = "(max-width: 479px)";
const mq = () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(NARROW) : null);
const subscribe = (cb) => {
    const m = mq();
    if (!m) return () => {};
    if (m.addEventListener) { m.addEventListener("change", cb); return () => m.removeEventListener("change", cb); }
    m.addListener(cb); return () => m.removeListener(cb);
};
export const useNarrow = () => useSyncExternalStore(subscribe, () => !!mq()?.matches, () => false);
const Wbr = () => (useNarrow() ? <wbr /> : null);
export const softBreaks = (text) => {
    if (typeof text !== "string") return text;
    const out = [];
    text.split(/(\s+)/).forEach((tok, i) => {
        // Satzzeichen am Wortende abtrennen, damit „Restrukturierung,“ auch greift
        const m = tok.match(/^(.*?)([.,:;!?]*)$/);
        const parts = JOINT_MAP.get(m[1]);
        if (!parts) { out.push(tok); return; }
        parts.forEach((part, j) => { if (j) out.push(<Wbr key={`w${i}-${j}`} />); out.push(part); });
        if (m[2]) out.push(m[2]);
    });
    return out;
};

// Testkennung aus dem Pfad: "/solution/ablauf" wird "solution-ablauf", "/" wird "home"
export const pageTestId = (path) => (path && path !== "/" ? path.replace(/^\//, "").replace(/\//g, "-") : "home");

// Ziel des Anfrage Buttons: /kontakt, bei Seiten mit lead Angabe mit Vorbelegung (?intent=&situation=&way=)
export const leadHref = (lead) => {
    if (!lead) return "/kontakt";
    const q = new URLSearchParams(Object.entries(lead).filter(([, v]) => v)).toString();
    return q ? `/kontakt?${q}` : "/kontakt";
};

// Gefüllter Hauptbutton wie „Weiter zur Anfrage“ (Klasse v3-lead-solid aus src/v3/css/lead.css)
const SolidCta = ({ to, testId, children }) => (
    <Link to={to} data-testid={testId} className="rl-btn rl-btn-primary rl-btn-lg v3-lead-solid">
        {children} <ArrowUpRight size={18} className="arrow" />
    </Link>
);

// Version 3: Platzhalterseite für einen Eintrag aus der Seitenliste (pages.js).
// page.notFound = true zeigt die schlichte Seite „Seite nicht gefunden“.
const Placeholder = ({ page }) => {
    const { lang } = useLanguage();
    const k = lang === "en" ? "en" : "de";
    const L = V3_PLACEHOLDER[k];
    const section = page?.menu ? V3_SECTIONS.find((s) => s.id === page.menu) : null;
    const showBack = section && section.to !== page.path;

    if (!page || page.notFound) {
        return (
            <main data-testid="v3-page-not-found" className="min-h-[70vh] pt-40 md:pt-52 pb-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <h1 className="rl-h1 m-0 text-ink">{softBreaks(L.notFound)}</h1>
                    <p className="rl-lead mt-6 m-0">{L.notFoundText}</p>
                    <div className="mt-10 flex flex-wrap items-center gap-6">
                        <SolidCta to="/kontakt" testId="v3-notfound-cta">{L.cta}</SolidCta>
                        <Link to="/" data-testid="v3-notfound-home" className="rl-link">{L.home}</Link>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main data-testid={`v3-page-${pageTestId(page.path)}`} className="min-h-[70vh] pt-40 md:pt-52 pb-24">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                {/* Rücklink als schlichte Brotkrume im Fließtextstil (keine Eyebrow) */}
                {showBack && (
                    <nav aria-label={k === "en" ? "Breadcrumb" : "Brotkrumen"} data-testid="v3-page-breadcrumb" className="mb-5 text-sm text-petrol-300">
                        <Link to={section.to} data-testid="v3-page-back" className="text-petrol-300 underline underline-offset-4 decoration-petrol-600 hover:text-ink hover:decoration-current transition-colors">{section[k]}</Link>
                        <span aria-hidden="true" className="mx-2">/</span>
                        <span aria-current="page">{page.label[k]}</span>
                    </nav>
                )}
                <h1 className="rl-h1 m-0 text-ink">{softBreaks(page.label[k])}</h1>
                <p className="rl-lead mt-6 m-0">{L.text}</p>
                <div className="mt-10">
                    <SolidCta to={leadHref(page.lead)} testId="v3-page-cta">{L.cta}</SolidCta>
                </div>
                {/* Abschnitte der Übersichtsseite (sections) mit id, damit die Sprungziele aus dem Menü schon jetzt greifen */}
                {page.sections?.length > 0 && (
                    <div className="mt-16 grid gap-px" data-testid="v3-page-sections">
                        {page.sections.map((sec) => (
                            <section key={sec.id} id={sec.id} className="scroll-mt-32 py-10 border-t border-petrol-700" data-testid={`v3-section-${sec.id}`}>
                                <h2 className="rl-h3 m-0 text-ink">{softBreaks(sec[k])}</h2>
                                <p className="mt-3 m-0 text-petrol-300">{L.text}</p>
                            </section>
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
};

export default Placeholder;
